/**
 * /notifications — the full log behind the header bell.
 *
 * Same source as the bell (`useNotifications`): the validation to-dos (runs you
 * own waiting at a gate, tests assigned to you that can be executed now), then
 * every pipeline run of the last seven days that was news, newest first, grouped
 * into failures and unattended completions. Each row shows when it happened,
 * whether it is still unread, and carries a delete button. Read and deleted
 * state are per-viewer and live in the browser only — deleting hides the row for
 * you, it does not touch the run or the test.
 */

import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Group,
  Loader,
  NavLink,
  Paper,
  Stack,
  Text,
  ThemeIcon,
  Title,
  Tooltip,
} from "@mantine/core";
import { IconCircleCheck, IconRefresh, IconTrash } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { notificationColor, notificationIcon, notificationWhen } from "@/app/notificationMeta";
import { type AppNotification, useNotifications } from "@/app/useNotifications";

export function NotificationsPage() {
  const {
    notifications,
    unreadCount,
    loading,
    isRead,
    reload,
    markRead,
    markAllRead,
    dismiss,
    dismissAll,
  } = useNotifications();
  const navigate = useNavigate();

  const gates = notifications.filter((n) => n.kind === "gate");
  const tests = notifications.filter((n) => n.kind === "test");
  const failed = notifications.filter((n) => n.kind === "failed");
  const finished = notifications.filter((n) => n.kind === "finished");

  const go = (n: AppNotification) => {
    markRead(n.id);
    navigate(n.to);
  };

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-start">
        <div>
          <Group gap="sm" align="center">
            <Title order={2}>Notifications</Title>
            {unreadCount > 0 && (
              <Badge color="red" variant="filled" size="lg">
                {unreadCount} unread
              </Badge>
            )}
          </Group>
          <Text c="dimmed" size="sm">
            Validation work waiting on you, and everything your pipelines have done in the last
            seven days. Marking read or deleting changes only what you see here — the runs and
            tests themselves are untouched.
          </Text>
        </div>
        <Group gap="xs">
          <Button
            variant="default"
            leftSection={<IconRefresh size={16} />}
            onClick={reload}
            loading={loading}
          >
            Refresh
          </Button>
          <Button variant="default" onClick={markAllRead} disabled={unreadCount === 0}>
            Mark all as read
          </Button>
          <Button
            variant="default"
            color="red"
            leftSection={<IconTrash size={16} />}
            onClick={dismissAll}
            disabled={notifications.length === 0}
          >
            Delete all
          </Button>
        </Group>
      </Group>

      {loading && notifications.length === 0 ? (
        <Group justify="center" p="xl">
          <Loader size="sm" />
        </Group>
      ) : notifications.length === 0 ? (
        <Paper withBorder radius="md" p="xl">
          <Stack align="center" gap="xs">
            <ThemeIcon variant="light" color="green" size={48} radius="xl">
              <IconCircleCheck size={26} />
            </ThemeIcon>
            <Text fw={600}>Nothing here</Text>
            <Text c="dimmed" size="sm">
              No run is waiting on a gate, no test is assigned to you, and no pipeline has failed or
              run unattended in the last seven days — or you have deleted everything that did.
            </Text>
          </Stack>
        </Paper>
      ) : (
        <>
          {gates.length > 0 && (
            <Section title={`Runs waiting on you (${gates.length})`}>
              {gates.map((n) => (
                <NotificationRow
                  key={n.id}
                  n={n}
                  read={isRead(n.id)}
                  onClick={() => go(n)}
                  onDelete={() => dismiss(n.id)}
                />
              ))}
            </Section>
          )}
          {tests.length > 0 && (
            <Section title={`Your assigned tests (${tests.length})`}>
              {tests.map((n) => (
                <NotificationRow
                  key={n.id}
                  n={n}
                  read={isRead(n.id)}
                  onClick={() => go(n)}
                  onDelete={() => dismiss(n.id)}
                />
              ))}
            </Section>
          )}
          {failed.length > 0 && (
            <Section title={`Runs that failed (${failed.length})`}>
              {failed.map((n) => (
                <NotificationRow
                  key={n.id}
                  n={n}
                  read={isRead(n.id)}
                  onClick={() => go(n)}
                  onDelete={() => dismiss(n.id)}
                />
              ))}
            </Section>
          )}
          {finished.length > 0 && (
            <Section title={`Finished while you were away (${finished.length})`}>
              {finished.map((n) => (
                <NotificationRow
                  key={n.id}
                  n={n}
                  read={isRead(n.id)}
                  onClick={() => go(n)}
                  onDelete={() => dismiss(n.id)}
                />
              ))}
            </Section>
          )}
        </>
      )}
    </Stack>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Paper withBorder radius="md" p="xs">
      <Text fw={600} size="sm" c="dimmed" px="sm" py={4}>
        {title}
      </Text>
      <Stack gap={2}>{children}</Stack>
    </Paper>
  );
}

function NotificationRow({
  n,
  read,
  onClick,
  onDelete,
}: {
  n: AppNotification;
  read: boolean;
  onClick: () => void;
  onDelete: () => void;
}) {
  return (
    <NavLink
      onClick={onClick}
      label={
        <Group gap="xs" wrap="nowrap" justify="space-between">
          <Text size="sm" fw={read ? 500 : 700} truncate>
            {n.title}
          </Text>
          <Text size="xs" c="dimmed" style={{ flexShrink: 0 }}>
            {notificationWhen(n.at)}
          </Text>
        </Group>
      }
      description={
        <Text size="xs" c="dimmed" truncate>
          {n.detail}
        </Text>
      }
      leftSection={
        <ThemeIcon variant="light" color={notificationColor(n)} size="lg" radius="xl">
          {notificationIcon(n, 18)}
        </ThemeIcon>
      }
      rightSection={
        <Group gap={6} wrap="nowrap" style={{ flexShrink: 0 }}>
          {!read && <Box w={8} h={8} style={{ borderRadius: "50%" }} bg="blue.6" />}
          <Tooltip label="Delete">
            <ActionIcon
              variant="subtle"
              color="gray"
              size="sm"
              aria-label={`Delete notification: ${n.title}`}
              // The row navigates; the button must not, or deleting would also
              // take you to the pipeline you just decided not to care about.
              onClick={(event) => {
                event.stopPropagation();
                event.preventDefault();
                onDelete();
              }}
            >
              <IconTrash size={14} />
            </ActionIcon>
          </Tooltip>
        </Group>
      }
      style={{ borderRadius: "var(--mantine-radius-sm)" }}
    />
  );
}
